import React, { useState, useEffect, useRef, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Alert,
  Badge,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Science as ScienceIcon,
  LocalHospital as HospitalIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ChildCare as ChildCareIcon,
  ArrowBack as ArrowBackIcon,
  PlaylistAddCheck as ActionsIcon
} from '@mui/icons-material';
import axios from 'axios';
import { loadLabPatients, loadLabRequestsOutpatient, loadLabRequestsInpatient, loadLabTestParameters, loadParameterDetails, saveLabResults, loadLabResults, 
  loadPatientLabReport, loadPatientLabResultsToday, loadPharmacyInfo, loadPatientDocumentation ,deleteResultC } from '../BaseURL';
import { generateLabReportHTML } from './LabReportTemplate';
import Swal from 'sweetalert2';
import LabReportDialog from './LabReportDialog';
import MedicalHistoryDialog from '../Doctors/MedicalHistoryDialog';

const MyPatients = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [quickSearchId, setQuickSearchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [patientLabs, setPatientLabs] = useState([]);

  /* Facility policy on unpaid orders, supplied by the request-list response.
     Strictest default so an older server, or a failed load, never leaves an
     unpaid order looking workable. */
  const [unpaidPolicy, setUnpaidPolicy] = useState({
    unpaidVisibility: 'hide',
    canServeUnpaid: false,
  });

  /* Unpaid comes from the server's normalised boolean. Never re-derived from
     status text: `status` on these rows is the workflow state, not payment. */
  const isUnpaidLab  = (lab) => lab?.isPaid === false;
  const isBlockedLab = (lab) => isUnpaidLab(lab) && !unpaidPolicy.canServeUnpaid;
  const [filterType, setFilterType] = useState('all');
  const [filterLoading, setFilterLoading] = useState(false);

  // Patient list table state
  const [patientPage, setPatientPage] = useState(0);
  const [patientRowsPerPage, setPatientRowsPerPage] = useState(10);
  const [patientOrderBy, setPatientOrderBy] = useState('fullName');
  const [patientOrder, setPatientOrder] = useState('asc');

  // Results Entry Dialog States
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedTestForResults, setSelectedTestForResults] = useState(null);
  const [testParameters, setTestParameters] = useState([]);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [parameterResult, setParameterResult] = useState('');
  const [parameterFlag, setParameterFlag] = useState('normal');
  const [enteredResults, setEnteredResults] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [loadingParameterDetails, setLoadingParameterDetails] = useState(false);
  const [testCategory, setTestCategory] = useState('');
  const [availableFlags, setAvailableFlags] = useState(['Select/Enter']);
  const [editingIndex, setEditingIndex] = useState(null);
  const [generalComment, setGeneralComment] = useState('');

  // Report Dialog States
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);

  // Documentation Dialog States
  const [documentationDialogOpen, setDocumentationDialogOpen] = useState(false);
  const [documentationData, setDocumentationData] = useState(null);
  const [documentationLoading, setDocumentationLoading] = useState(false);

  // Medical History Dialog State
  const [medicalHistoryOpen, setMedicalHistoryOpen] = useState(false);

  // Pagination for labs list
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Company info for reports
  const [companyInfo, setCompanyInfo] = useState(null);

  const searchInputRef = useRef(null);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

  // Load patients once on mount — filter is applied client-side for speed
  useEffect(() => {
    loadPatients();
  }, []);

  // Load company info on mount
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await fetch(loadPharmacyInfo);
        const data = await response.json();
        if (data.success && data.results) {
          setCompanyInfo(data.results);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyInfo();
  }, []);

  const loadPatients = async () => {
    setFilterLoading(true);
    try {
      const response = await axios.get(loadLabPatients);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('❌ Error loading patients:', error);
      setSearchResults([]);
    } finally {
      setFilterLoading(false);
    }
  };

  // Load patient labs when a *different* patient is selected. Depend on the
  // id/visitDate (primitives) rather than the whole object — merging vitals
  // into selectedPatient produces a new object ref and would otherwise loop.
  useEffect(() => {
    if (selectedPatient?.id) {
      loadPatientLabs(selectedPatient.id, selectedPatient.visitDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.id, selectedPatient?.visitDate]);

  // Check for pending lab edit when labs are loaded
  useEffect(() => {
    const pendingEdit = localStorage.getItem('pendingLabEdit');
    if (pendingEdit && patientLabs.length > 0) {
      try {
        const editData = JSON.parse(pendingEdit);
        // Find the matching lab test
        const labToEdit = patientLabs.find(lab => lab.test_name === editData.testName);
        if (labToEdit) {
          // Automatically open the results dialog for this test
          handleEnterResults(labToEdit);
        }
        localStorage.removeItem('pendingLabEdit');
      } catch (error) {
        console.error('Error parsing pending lab edit data:', error);
        localStorage.removeItem('pendingLabEdit');
      }
    }
  }, [patientLabs]);

  // Check for lab edit data from history
  useEffect(() => {
    const labEditData = localStorage.getItem('labEditData');
    if (labEditData) {
      try {
        const editData = JSON.parse(labEditData);
        // Find and select the patient
        const patientToSelect = searchResults.find(patient => patient.id === editData.patientId);
        if (patientToSelect) {
          setSelectedPatient(patientToSelect);
          // Store the edit data for later use when labs are loaded
          setTimeout(() => {
            localStorage.setItem('pendingLabEdit', JSON.stringify(editData));
          }, 100);
          // Clear the data so it doesn't trigger again
          localStorage.removeItem('labEditData');
        }
      } catch (error) {
        console.error('Error parsing lab edit data:', error);
        localStorage.removeItem('labEditData');
      }
    }
  }, [searchResults]);


  // Quick lookup by patient ID from the header — lets staff jump to another
  // patient's investigations without bouncing back to the worklist. Patient IDs
  // carry a prefix (e.g. "OPD-125"), so we match on the digits only so staff
  // can just type the number.
  const handleQuickSearchById = () => {
    const idQuery = quickSearchId.trim();
    if (!idQuery) return;

    const digitsOf = (val) => String(val ?? '').replace(/\D/g, '');
    const queryDigits = digitsOf(idQuery);

    const match = searchResults.find((p) => {
      if (String(p.id).toLowerCase() === idQuery.toLowerCase()) return true;
      const idDigits = digitsOf(p.id);
      return queryDigits && idDigits && idDigits === queryDigits;
    });

    if (match) {
      setSelectedPatient(match);
      setQuickSearchId('');
    } else {
      Swal.fire({
        icon: 'info',
        title: 'No patient found',
        text: `No patient with ID "${idQuery}" was found on the active investigations list.`,
        confirmButtonColor: '#1A9B7F',
      });
    }
  };

  const loadPatientLabs = async (patientId, visitDate) => {
  setLoading(true);

  try {
    // State routing — normalise casing before comparing. The DB writes
    // `inPatient` (lowercase i, capital P) which matched neither of the
    // previous string literals, so every admitted patient was silently
    // routed to the outpatient endpoint.
    const normalisedState = String(selectedPatient.state || '').toLowerCase();
    const isInpatient = normalisedState === 'inpatient';
    let response;
    if (isInpatient) {
      response = await axios.post(loadLabRequestsInpatient, { patientId, visitDate });
    } else {
      response = await axios.post(loadLabRequestsOutpatient, { patientId, visitDate });
    }
    
    // NEW: Handle the updated response structure
    // Response now includes patient info, labRequests array, and totalRequests
    console.log('📦 [MYPATIENTS] Lab requests response:', response.data);
    
    // Policy arrives on the SAME response as the rows, so it always describes
    // the list being shown. Absent => an older server: assume the strictest
    // position rather than letting an unpaid order look workable.
    setUnpaidPolicy({
      unpaidVisibility: response.data?.unpaidVisibility || 'hide',
      canServeUnpaid:   response.data?.canServeUnpaid === true,
    });

    if (response.data.labRequests) {
      setPatientLabs(response.data.labRequests);
      console.log('✅ [MYPATIENTS] Loaded', response.data.labRequests.length, 'lab requests');
    } else if (response.data.labRequests === undefined && Array.isArray(response.data)) {
      // Fallback for old response format (if backend not updated yet)
      setPatientLabs(response.data);
    } else {
      setPatientLabs([]);
    }

    // Merge vitals from the per-patient response into selectedPatient.
    // The list endpoint no longer ships vitals (kept the list query fast),
    // so we hydrate them here when the chart opens.
    const vitalsFromDetail = response.data?.patient?.vitals;
    if (vitalsFromDetail) {
      setSelectedPatient(prev => (prev && prev.id === patientId)
        ? { ...prev, vitalSigns: vitalsFromDetail }
        : prev);
    }
  } catch (error) {
    console.error('❌ [MYPATIENTS] Error loading patient labs:', error);
    setPatientLabs([]);

    // Surface the real backend message so the front-office / dev doesn't
    // have to open the browser console to find out what went wrong.
    const serverMsg = error?.response?.data?.message
      || error?.response?.data?.error
      || error?.message
      || 'Unknown error';
    Swal.fire({
      icon: 'error',
      title: 'Error Loading Labs',
      html: `Failed to load laboratory requests.<br/><br/>
             <span style="color:#64748b;font-size:0.85rem">${serverMsg}</span>`,
      confirmButtonColor: '#d33'
    });
  } finally {
    setLoading(false);
  }
};


  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
  };

  // Normalize the state field returned by the backend ('inpatient' / 'outpatient')
  // into a stable canonical form used by the table & filters.
  const canonicalState = (raw) => {
    const s = String(raw || '').toLowerCase();
    if (s === 'inpatient') return 'InPatient';
    if (s === 'outpatient') return 'OutPatient';
    return raw || '—';
  };

  // Counts for the filter chips
  const patientCounts = useMemo(() => {
    const c = { all: searchResults.length, InPatient: 0, OutPatient: 0 };
    searchResults.forEach(p => {
      const s = canonicalState(p.state);
      if (s === 'InPatient') c.InPatient++;
      else if (s === 'OutPatient') c.OutPatient++;
    });
    return c;
  }, [searchResults]);

  // Apply search + status filter, then sort
  const visiblePatients = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    const filtered = searchResults.filter(p => {
      const s = canonicalState(p.state);
      if (filterType === 'InPatient' && s !== 'InPatient') return false;
      if (filterType === 'OutPatient' && s !== 'OutPatient') return false;
      if (!q) return true;
      return (
        (p.fullName || '').toLowerCase().includes(q)
        || String(p.id || '').toLowerCase().includes(q)
        || String(p.tel || '').includes(q)
        || (p.insurance || '').toLowerCase().includes(q)
      );
    });
    const cmp = (a, b) => {
      const av = patientOrderBy === 'state' ? canonicalState(a.state) : a[patientOrderBy];
      const bv = patientOrderBy === 'state' ? canonicalState(b.state) : b[patientOrderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    filtered.sort((a, b) => patientOrder === 'asc' ? cmp(a, b) : -cmp(a, b));
    return filtered;
  }, [searchResults, searchTerm, filterType, patientOrderBy, patientOrder]);

  const pagedPatients = visiblePatients.slice(
    patientPage * patientRowsPerPage,
    patientPage * patientRowsPerPage + patientRowsPerPage
  );

  const handlePatientSort = (field) => {
    if (patientOrderBy === field) {
      setPatientOrder(patientOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPatientOrderBy(field);
      setPatientOrder('asc');
    }
  };

  // Reset to first page if filters shrink the list past the current page
  useEffect(() => {
    if (patientPage > 0 && pagedPatients.length === 0 && visiblePatients.length > 0) {
      setPatientPage(0);
    }
  }, [pagedPatients.length, visiblePatients.length, patientPage]);

  const initialsOf = (name = '') =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

  // Results Entry Functions
  const handleEnterResults = async (lab) => {
    setSelectedTestForResults(lab);
    setResultsDialogOpen(true);
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setEnteredResults([]);
    setAvailableFlags(['Select/Enter']);
    setGeneralComment(''); // Reset general comment
    setEditingIndex(null); // Reset editing index to enable parameter selection

    // Load distinct parameters for this test (following Java pattern)
    const category = await loadTestParameters(lab.test_name);

    // Load existing results for this patient and test, passing both lab_no and category
    await loadExistingResults(lab, category);
  };

 const loadTestParameters = async (testName) => {
  setLoadingParameters(true);

  try {
    const response = await axios.get(loadLabTestParameters, {
      params: { name: testName }
    });

    if (response.data.success) {
      setTestParameters(response.data.parameters);
      setTestCategory(response.data.category);
      return response.data.category;
    }

  } catch (error) {
    console.error(error);
    setTestParameters([]);
    setTestCategory('');
    return '';

  } finally {
    setLoadingParameters(false);
  }
};


 const loadExistingResults = async (lab, category) => {
  try {
    const labDate = lab.requested_date || new Date().toISOString().split('T')[0];

    const response = await axios.get(loadLabResults, {
      params: {
        patientId: selectedPatient.id,
        testCategory: category,
        date: labDate,
        labNo: lab.lab_no
      }
    });

    if (response.data.success) {
      setEnteredResults(response.data.results || []);
      setGeneralComment(response.data.generalComment || '');
    }

  } catch (error) {
    console.error(error);
    setEnteredResults([]);
    setGeneralComment('');
  }
};


 const handleParameterSelect = async (parameter) => {
  setLoadingParameterDetails(true);

  try {
    const response = await axios.get(loadParameterDetails, {
      params: {
        testName: selectedTestForResults.test_name,
        parameterName: parameter.test
      }
    });

    if (response.data.success) {
      const { parameter: paramDetails, flags = [] } = response.data;

      setSelectedParameter(paramDetails);

      // add default option first
      setAvailableFlags(['Select/Enter', ...flags]);

      setParameterResult('');
      setParameterFlag('Select/Enter');
    }

  } catch (error) {
    console.error('Error loading parameter details:', error);

    setSelectedParameter(null);
    setAvailableFlags(['Select/Enter']);

  } finally {
    setLoadingParameterDetails(false);
  }
};


  const handleAddResult = () => {
    if (!selectedParameter || !parameterResult.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a parameter and enter a result.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const newResult = {
      parameterName: selectedParameter.test,
      result: parameterResult,
      unit: selectedParameter.unit || '',
      range: getParameterRange(selectedParameter),
      flag: parameterFlag
    };

    if (editingIndex !== null) {
      // Update existing result - preserve trans_code for database update
      const resultToUpdate = enteredResults[editingIndex];
      if (resultToUpdate.trans_code) {
        newResult.trans_code = resultToUpdate.trans_code;
      }
      const updatedResults = [...enteredResults];
      updatedResults[editingIndex] = newResult;
      setEnteredResults(updatedResults);
      setEditingIndex(null);
    } else {
      // Check for duplicate parameter (only when adding new)
      const isDuplicate = enteredResults.some(result => result.parameterName === selectedParameter.test);
      if (isDuplicate) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate Parameter',
          text: 'This parameter has already been added. Use edit to modify the existing result.',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      // Add new result
      setEnteredResults([...enteredResults, newResult]);
    }

    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setAvailableFlags(['Select/Enter']);
  };

  const getParameterRange = (parameter) => {
    // Logic to determine range based on patient demographics (following Java pattern)
    const patientGender = selectedPatient?.gender?.toUpperCase();

    if (patientGender === 'MALE' && parameter.male) {
      return parameter.male;
    } else if (patientGender === 'FEMALE' && parameter.female) {
      return parameter.female;
    } else if (parameter.children) {
      return parameter.children;
    } else {
      return 'Not specified';
    }
  };

  const detectFlagFromResult = (result, range) => {
    if (!result || !result.trim()) {
      return 'Select/Enter';
    }

    if (range === 'Not specified') {
      return 'normal'; // Default to normal when no range is specified
    }

    // Parse range - handle various formats like "13.0-17.0", "13-17", "4.0 - 11.0", etc.
    const rangeMatch = range.match(/(\d*\.?\d+)\s*-\s*(\d*\.?\d+)/);
    if (!rangeMatch) {
      return 'normal'; // If range format is not recognized, default to normal
    }

    const minValue = parseFloat(rangeMatch[1]);
    const maxValue = parseFloat(rangeMatch[2]);
    const resultValue = parseFloat(result.trim());

    if (isNaN(resultValue) || isNaN(minValue) || isNaN(maxValue)) {
      return 'normal';
    }

    if (resultValue < minValue) {
      return 'low';
    } else if (resultValue > maxValue) {
      return 'high';
    } else {
      return 'normal';
    }
  };

  const handleFlagChange = (value) => {
    setParameterFlag(value);

    // If range is "Not specified", set result to the selected flag value
    if (selectedParameter) {
      const range = getParameterRange(selectedParameter);
      if (range === 'Not specified') {
        setParameterResult(value); // Result becomes the flag value
      }
    }
  };

  const handleResultChange = (value) => {
    setParameterResult(value);

    // Auto-detect flag based on result and range (only for parameters with ranges)
    if (selectedParameter) {
      const range = getParameterRange(selectedParameter);
      if (range !== 'Not specified') {
        const detectedFlag = detectFlagFromResult(value, range);
        setParameterFlag(detectedFlag);
      }
      // For parameters with no range, flag determines the result, not vice versa
    }
  };

  const handleClearResult = () => {
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setAvailableFlags(['Select/Enter']);
  };

  const handleSaveResults = async () => {
    if (enteredResults.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Results to Save',
        text: 'Please add at least one test result before saving.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Save',
      text: `Are you sure you want to save ${enteredResults.length} test result(s) for ${selectedTestForResults.test_name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save Results',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Show loading dialog
    Swal.fire({
      title: 'Saving Results...',
      text: 'Please wait while we save your test results.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await axios.post(saveLabResults, {
        patientId: selectedPatient.id,
        testName: selectedTestForResults.test_name,
        testCategory: testCategory, // Include test category for proper result categorization
        lab_no: selectedTestForResults.lab_no,
        results: enteredResults,
        enteredBy: userInfo.name ,
        // enteredDate omitted on purpose. The old code sent
        // `selectedTestForResults.requested_date`, which back-dated
        // lab_analysis.dat to the day the sample was REQUESTED — so the
        // Lab worklist's "visible for 24 h from result entry" filter
        // compared against a date days in the past and dropped the row
        // the moment you clicked Save. Letting the backend default
        // `date` to today (server.js:18882) means la.dat reflects when
        // results were actually entered, which is what the worklist
        // window and the audit trail should track.
        generalComment: generalComment, // Add general comment
        patientInfo: {
          name: selectedPatient.fullName,
          age: selectedPatient.age,
          gender: selectedPatient.gender,
          path_no: selectedPatient.id,
          other_comments: ''
        }
      });

      if (response.data.success) {
        
        Swal.fire({
          icon: 'success',
          title: 'Results Saved Successfully!',
          text: `${enteredResults.length} test result(s) have been saved.`,
          confirmButtonColor: '#3085d6',
          timer: 3000,
          timerProgressBar: true
        });

        setResultsDialogOpen(false);
        setSelectedTestForResults(null);
        setEnteredResults([]);
        setGeneralComment(''); // Reset general comment
        setTestParameters([]); // Clear test parameters
        setTestCategory(''); // Clear test category
        setSelectedParameter(null); // Clear selected parameter
        setParameterResult(''); // Clear parameter result
        setParameterFlag('normal'); // Reset flag
        setAvailableFlags(['Select/Enter']); // Reset flags
        setEditingIndex(null); // Reset editing index
        // Optionally reload the lab requests to show updated status
        loadPatientLabs(selectedPatient.id, selectedPatient.visitDate);
      }
    } catch (error) {
      console.error('Error saving results:', error);

      /* 409 means the bill is unpaid, not that anything is broken. Over a list
         of test names a red "Error Saving Results" reads as a system fault and
         sends the technician to IT instead of to the cash desk. Warning icon,
         "Payment required", and reload — the cash desk may have taken payment
         while this dialog sat open. */
      if (error.response?.status === 409 && error.response.data?.paymentRequired) {
        await Swal.fire({
          icon: 'warning',
          title: 'Payment required',
          text: error.response.data.message
            || 'Payment has not been recorded for this test yet.',
          confirmButtonColor: '#b45309',
        });
        setResultsDialogOpen(false);
        if (selectedPatient) {
          loadPatientLabs(selectedPatient.id, selectedPatient.visitDate);
        }
        return;
      }

      // Surface the server-side reason. Axios wraps HTTP errors so
      // `error.message` is just "Request failed with status code 500";
      // the actual DB/validation message lives on `error.response.data`.
      const serverMsg = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'An error occurred while saving the results. Please try again.';

      Swal.fire({
        icon: 'error',
        title: 'Error Saving Results',
        text: serverMsg,
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleEditResult = (index) => {
    const resultToEdit = enteredResults[index];
    setEditingIndex(index);

    // Find the parameter in testParameters to get full details
    const parameter = testParameters.find(param => param.test === resultToEdit.parameterName);
    if (parameter) {
      setSelectedParameter(parameter);
      setParameterResult(resultToEdit.result);
      setParameterFlag(resultToEdit.flag);
      
      // trans_code will be preserved and sent to backend with update
      if (resultToEdit.trans_code) {
        // trans_code exists - this will trigger UPDATE instead of INSERT
      }

      // Load flags for this parameter
      handleParameterSelect(parameter);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('Select/Enter');
    setAvailableFlags(['Select/Enter']);
  };


  const handleRemoveResult = async (resultk) => {
  // Check if result has been saved to database (has trans_code)
  if (!resultk.trans_code) {
    // Result not yet saved to database - just remove from local state
    const updatedResults = enteredResults.filter((r) => r !== resultk);
    setEnteredResults(updatedResults);
    
    Swal.fire({
      icon: 'success',
      title: 'Result Removed',
      text: 'The test result has been removed successfully.',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false
    });
    return;
  }

  // Result exists in database - confirm and delete
  Swal.fire({
    title: 'Remove Result',
    text: 'Are you sure you want to remove this test result? This action cannot be undone.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, Remove',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      // Show loading
      Swal.fire({
        title: 'Deleting Result...',
        text: 'Please wait while we remove the test result.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // Delete from database
        const response = await axios.delete(deleteResultC(resultk.trans_code));

        if (response.data.success) {
          // Remove from local state
          const updatedResults = enteredResults.filter((r) => r.trans_code !== resultk.trans_code);
          setEnteredResults(updatedResults);

          Swal.fire({
            icon: 'success',
            title: 'Result Removed',
            text: 'The test result has been removed successfully from the database.',
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: false
          });

          // Optionally reload the patient labs to reflect the change
          if (selectedPatient && selectedTestForResults) {
            await loadExistingResults(selectedTestForResults, testCategory);
          }
        } else {
          throw new Error(response.data.message || 'Failed to delete result');
        }
      } catch (error) {
        console.error('❌ Error deleting result:', error);
        
        Swal.fire({
          icon: 'error',
          title: 'Error Deleting Result',
          text: error.response?.data?.message || error.message || 'Failed to delete the test result. Please try again.',
          confirmButtonColor: '#d33'
        });
      }
    }
  });
};

  // Report Functions
  const handleViewReport = async () => {
    if (!selectedPatient) return;

    setReportLoading(true);
    try {
      const response = await fetch(loadPatientLabReport, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setReportData(data.reportData || {});
        setReportDialogOpen(true);
      }
      setReportLoading(false);
    } catch (error) {
      setReportLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load lab report. Please try again.',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleCloseReport = () => {
    setReportDialogOpen(false);
    setReportData({});
  };

  const handleViewTodayLabResults = async () => {
    try {
      const response = await fetch(loadPatientLabResultsToday, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient?.id
        })
      });

      const data = await response.json();

      if (data.success && data.reportData && Object.keys(data.reportData).length > 0) {
        // Generate HTML report and open in new window
        const htmlContent = generateLabReportHTML(data.reportData, selectedPatient, userInfo, companyInfo);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Results',
          text: 'No lab results found for today.'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load today\'s lab results.'
      });
    }
  };

  const handleViewDocumentation = async () => {
    if (!selectedPatient) {
      Swal.fire({
        icon: 'warning',
        title: 'No Patient Selected',
        text: 'Please select a patient first.',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    setDocumentationLoading(true);
    try {
      const response = await axios.get(loadPatientDocumentation, {
        params: { patientId: selectedPatient.id }
      });

      if (response.data.success && response.data.data) {
        setDocumentationData(response.data.data);
        setDocumentationDialogOpen(true);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Documentation',
          text: 'No documentation records found for this patient.',
          confirmButtonColor: '#1976d2'
        });
      }
    } catch (error) {
      console.error('Error loading documentation:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load patient documentation.',
        confirmButtonColor: '#d32f2f'
      });
    } finally {
      setDocumentationLoading(false);
    }
  };

  const handleCloseDocumentation = () => {
    setDocumentationDialogOpen(false);
    setDocumentationData(null);
  };



  // Clears the current selection and returns to the worklist view.
  const handleBackToWorklist = () => {
    setSelectedPatient(null);
    setPatientLabs([]);
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Page header — toggles between worklist title and patient breadcrumb+back */}
      {selectedPatient ? (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            border: '1px solid #e5e9ef',
            borderRadius: 2.5,
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
          }}
        >
          <Button
            onClick={handleBackToWorklist}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#1A9B7F',
              borderRadius: 1.75,
              px: 1.75,
              '&:hover': { bgcolor: 'rgba(26,155,127,0.08)' },
            }}
          >
            Back to Worklist
          </Button>
          <Box sx={{ width: '1px', height: 28, bgcolor: '#e5e9ef' }} />
          <Box sx={{ minWidth: 0, flexShrink: 1 }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700,
              color: '#1A9B7F', letterSpacing: '1.4px',
              textTransform: 'uppercase', lineHeight: 1,
            }}>
              Patient Investigations
            </Typography>
            <Typography sx={{
              fontSize: { xs: '1rem', md: '1.15rem' },
              fontWeight: 700,
              color: '#0f172a',
              mt: 0.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {selectedPatient.fullName}
              <Box component="span" sx={{ fontWeight: 500, color: '#64748b', ml: 1, fontSize: '0.88rem' }}>
                · ID {selectedPatient.id}
              </Box>
            </Typography>
          </Box>

          {/* Quick-jump search: type an ID, hit Enter or Search, load that patient's investigations */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <TextField
              size="small"
              value={quickSearchId}
              onChange={(e) => setQuickSearchId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickSearchById();
                }
              }}
              placeholder="Patient ID…"
              sx={{
                width: { xs: 140, md: 180 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearchId && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setQuickSearchId('')}
                      sx={{ color: '#94a3b8' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              onClick={handleQuickSearchById}
              disabled={!quickSearchId.trim()}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.75,
                bgcolor: '#1A9B7F',
                px: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#15826A', boxShadow: 'none' },
              }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      ) : (
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          My Patients
        </Typography>
      )}

      {/* Patient List — only visible when no patient is selected */}
      {!selectedPatient && (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 2.5 },
          border: '1px solid #e5e9ef',
          borderRadius: 3,
          bgcolor: '#fff',
          boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: '0.72rem', fontWeight: 700,
            color: '#1A9B7F', letterSpacing: '1.4px',
            textTransform: 'uppercase', mb: 0.5,
          }}>
            Investigation Workspace
          </Typography>
          <Typography sx={{
            fontSize: { xs: '1.25rem', md: '1.45rem' },
            fontWeight: 700, color: '#0f172a',
            letterSpacing: '-0.3px', lineHeight: 1.2,
            mb: 0.5,
          }}>
            Patient Worklist
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.88rem' }}>
            {(() => {
              const n = searchResults.length;
              if (filterLoading && n === 0) return 'Loading patients…';
              if (n === 0) return 'No patients currently have active investigations.';
              const word = n === 1 ? 'patient' : 'patients';
              return `${n} ${word} with active investigations. Click any row to open the chart.`;
            })()}
          </Typography>
        </Box>

        {/* Filter chips + search */}
        <Paper elevation={0} sx={{
          p: 1.5, mb: 2,
          border: '1px solid #e5e9ef', borderRadius: 2.5, bgcolor: '#fff',
        }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
              {[
                { key: 'all',        label: 'All',        count: patientCounts.all,        color: '#475569' },
                { key: 'InPatient',  label: 'Inpatient',  count: patientCounts.InPatient,  color: '#15803D' },
                { key: 'OutPatient', label: 'Outpatient', count: patientCounts.OutPatient, color: '#A16207' },
              ].map(f => {
                const active = filterType === f.key;
                return (
                  <Chip
                    key={f.key}
                    label={`${f.label} (${f.count})`}
                    onClick={() => { setFilterType(f.key); setPatientPage(0); }}
                    size="small"
                    sx={{
                      height: 30,
                      fontWeight: 600, fontSize: '0.78rem',
                      bgcolor: active ? f.color : '#fff',
                      color:   active ? '#fff' : '#334155',
                      border: '1px solid',
                      borderColor: active ? f.color : '#e5e9ef',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: active ? f.color : `${f.color}0D`, borderColor: f.color },
                    }}
                  />
                );
              })}
            </Stack>

            <TextField
              size="small"
              inputRef={searchInputRef}
              placeholder="Search by name, ID, phone or insurance…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPatientPage(0); }}
              sx={{
                width: { xs: '100%', md: 340 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: '#94a3b8' }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>

        {/* Patient table */}
        <Paper elevation={0} sx={{
          border: '1px solid #e5e9ef', borderRadius: 2.5, overflow: 'hidden', bgcolor: '#fff',
        }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: '1px solid #e5e9ef' } }}>
                  {[
                    { id: 'fullName',  label: 'Patient',      sortable: true,  width: '28%' },
                    { id: 'age',       label: 'Age / Gender', sortable: true,  width: '14%' },
                    { id: 'state',     label: 'Status',       sortable: true,  width: '12%' },
                    { id: 'insurance', label: 'Insurance',    sortable: true,  width: '18%' },
                    { id: 'visitDate', label: 'Visit Date',   sortable: true,  width: '14%' },
                    { id: 'tel',       label: 'Phone',        sortable: false, width: '12%' },
                    { id: 'actions',   label: '',             sortable: false, width: '2%', align: 'right' },
                  ].map(c => (
                    <TableCell
                      key={c.id}
                      align={c.align || 'left'}
                      sx={{
                        width: c.width,
                        fontWeight: 700, fontSize: '0.72rem',
                        color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px',
                        py: 1.25,
                      }}
                    >
                      {c.sortable ? (
                        <TableSortLabel
                          active={patientOrderBy === c.id}
                          direction={patientOrderBy === c.id ? patientOrder : 'asc'}
                          onClick={() => handlePatientSort(c.id)}
                          sx={{ '& .MuiTableSortLabel-icon': { fontSize: 16 } }}
                        >
                          {c.label}
                        </TableSortLabel>
                      ) : c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filterLoading && searchResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                      <CircularProgress size={28} sx={{ color: '#1A9B7F' }} />
                      <Typography sx={{ color: '#64748b', fontSize: '0.9rem', mt: 1.5 }}>
                        Loading patients…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedPatients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                      <PersonIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                      <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                        {searchTerm || filterType !== 'all'
                          ? 'No patients match your filters.'
                          : 'No patients to show.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedPatients.map((p) => {
                  const state = canonicalState(p.state);
                  const isInpatient = state === 'InPatient';
                  const isChild = (p.age != null) && Number(p.age) <= 12;
                  const isSelected = selectedPatient && selectedPatient.id === p.id;
                  return (
                    <TableRow
                      key={p.id}
                      hover
                      onClick={() => handlePatientSelect(p)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'rgba(26,155,127,0.08)' : 'transparent',
                        '& td': { borderBottom: '1px solid #f1f5f9', py: 1.25 },
                        '&:hover .row-arrow': { opacity: 1, transform: 'translateX(2px)' },
                        '&:last-child td': { borderBottom: 'none' },
                      }}
                    >
                      {/* Patient (avatar + name + ID) */}
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{
                            width: 36, height: 36, fontSize: 13, fontWeight: 700,
                            bgcolor: isChild ? '#FCE7F3' : 'rgba(26,155,127,0.12)',
                            color:   isChild ? '#BE185D' : '#1A9B7F',
                          }}>
                            {isChild ? <ChildCareIcon sx={{ fontSize: 18 }} /> : initialsOf(p.fullName)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{
                              fontWeight: 600, fontSize: '0.88rem', color: '#0f172a',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {p.fullName || 'Unknown'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                              ID: {p.id}{p.a_name ? ` · Ref: ${p.a_name}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Age / Gender */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                          {p.age ?? '—'} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}>yrs</Box>
                        </Typography>
                        <Chip
                          label={p.gender || '—'}
                          size="small"
                          sx={{
                            mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                            bgcolor: String(p.gender).toUpperCase() === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                            color:   String(p.gender).toUpperCase() === 'MALE' ? '#1D4ED8' : '#BE185D',
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={state}
                          size="small"
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 700,
                            bgcolor: isInpatient ? '#DCFCE7' : '#FEF3C7',
                            color:   isInpatient ? '#15803D' : '#A16207',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* Insurance */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
                          {p.insurance || '—'}
                        </Typography>
                      </TableCell>

                      {/* Visit Date */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', color: '#334155' }}>
                          {p.visitDate
                            ? new Date(p.visitDate).toISOString().slice(0, 10)
                            : '—'}
                        </Typography>
                      </TableCell>

                      {/* Phone */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
                          {p.tel || '—'}
                        </Typography>
                      </TableCell>

                      {/* Action arrow */}
                      <TableCell align="right">
                        <Tooltip title="Open chart" arrow>
                          <IconButton
                            size="small"
                            className="row-arrow"
                            onClick={(e) => { e.stopPropagation(); handlePatientSelect(p); }}
                            sx={{
                              color: '#1A9B7F',
                              opacity: 0.55,
                              transition: 'opacity .15s ease, transform .15s ease',
                              '&:hover': { bgcolor: 'rgba(26,155,127,0.12)', opacity: 1 },
                            }}
                          >
                            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {visiblePatients.length > patientRowsPerPage && (
            <TablePagination
              component="div"
              count={visiblePatients.length}
              page={patientPage}
              onPageChange={(_, p) => setPatientPage(p)}
              rowsPerPage={patientRowsPerPage}
              onRowsPerPageChange={(e) => { setPatientRowsPerPage(parseInt(e.target.value, 10)); setPatientPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #e5e9ef' }}
            />
          )}
        </Paper>
      </Paper>
      )}

      {/* Selected Patient Information */}
     {selectedPatient && (
  <Card
    sx={{
      mb: 3,
      borderRadius: 4,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
        zIndex: 1
      }
    }}
  >
    <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
      <Grid container spacing={2} wrap="nowrap">
        {/* Patient Profile Section */}
        <Grid item xs={2.4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #0fd34aff 0%, #319249ff 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                mb: 2,
                fontSize: '2.5rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(10px)',
                zIndex: 1
              }}
            >
              {selectedPatient.fullName?.split(" ").map(n => n[0]).join("").slice(0, 2) || 'P'}
            </Avatar>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 1 }}>
              {selectedPatient.fullName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, opacity: 0.95, zIndex: 1 }}>
              {selectedPatient.age} years old
            </Typography>
            <Box
              sx={{
                px: 2.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                mb: 2,
                zIndex: 1
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                ID: {selectedPatient.id}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2, zIndex: 1, flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              <Chip
                label={selectedPatient.gender}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Chip
                label={selectedPatient.insurance}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              />
            </Stack>
            <Chip
              label={selectedPatient.state}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                color: 'white',
                fontWeight: 600,
                px: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(10px)',
                zIndex: 1
              }}
            />
          </Box>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#f8f9fd',
              border: '1px solid #e8eaf6',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  mr: 2
                }}
              >
                <PhoneIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary">
                Contact Info
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Phone Number
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                  {selectedPatient.tel || 'Not provided'}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Location
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                  {selectedPatient.res || 'Not provided'}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Visit Date
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, color: 'primary.main' }}>
                  {selectedPatient.visitDate}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Vital Signs Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#fff3e0',
              border: '1px solid #ffe0b2',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(255, 152, 0, 0.15)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  mr: 2
                }}
              >
                <HospitalIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#e65100' }}>
                Vital Signs
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Temperature
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.temperature || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Blood Pressure
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.bp || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Pulse
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.pulse || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Respiratory Rate
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.respiration || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    color: '#e65100'
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Additional Vitals Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#e8f5e9',
              border: '1px solid #c8e6c9',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(76, 175, 80, 0.15)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  mr: 2
                }}
              >
                <ScienceIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                Measurements
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Weight
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.weight || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(76, 175, 80, 0.15)',
                    color: '#2e7d32'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Height
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.height || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(76, 175, 80, 0.15)',
                    color: '#2e7d32'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  BMI
                </Typography>
                <Chip
                  label={selectedPatient.vitalSigns?.bmi || '-'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'rgba(76, 175, 80, 0.15)',
                    color: '#2e7d32'
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Actions Section */}
        <Grid item xs={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              bgcolor: '#f3e8ff',
              border: '1px solid #ddd6fe',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(124, 58, 237, 0.18)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                  mr: 2
                }}
              >
                <ActionsIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#5b21b6' }}>
                Actions
              </Typography>
            </Box>

            <Stack spacing={1.25}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={handleViewTodayLabResults}
                disabled={patientLabs.length === 0}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  backgroundColor: '#43a047',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#388e3c', boxShadow: '0 4px 12px rgba(67,160,71,0.25)' }
                }}
              >
                Today's Results
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={handleViewReport}
                disabled={reportLoading || patientLabs.length === 0}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  backgroundColor: '#1976d2',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#1565c0', boxShadow: '0 4px 12px rgba(25,118,210,0.25)' }
                }}
              >
                {reportLoading ? <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> : null}
                View Report
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => handleViewDocumentation()}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  borderColor: '#ff9800', color: '#ff9800',
                  '&:hover': {
                    borderColor: '#f57c00',
                    backgroundColor: 'rgba(255, 152, 0, 0.06)'
                  }
                }}
              >
                View Documentation
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => setMedicalHistoryOpen(true)}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                  py: 0.9,
                  borderColor: '#7c3aed', color: '#7c3aed',
                  '&:hover': {
                    borderColor: '#5b21b6',
                    backgroundColor: 'rgba(124, 58, 237, 0.06)'
                  }
                }}
              >
                Medical History
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
)}

      {/* Requested Labs Section */}
      {selectedPatient && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Requested Laboratory Tests
              </Typography>
              <Chip
                label={`${patientLabs.length} test${patientLabs.length === 1 ? '' : 's'}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>

           {loading ? (
  <Box display="flex" justifyContent="center" p={3}>
    <CircularProgress />
  </Box>
) : patientLabs.length === 0 ? (
  <Alert severity="info">
    No laboratory tests found for this patient within their active time windows.
  </Alert>
) : (
  <>
    <List>
      {patientLabs
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((lab, index) => (
        <React.Fragment key={`${lab.patient_id || lab.id}_${lab.test_name}_${lab.requested_date || index}`}>
          <ListItemButton
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1" fontWeight={600}>
                    {lab.test_name} {lab.category && `(${lab.category})`}
                  </Typography>

                  {/* Unpaid badge. Two wordings on purpose — "may run" and
                      "payment required" are different instructions to the
                      technician. A row that looks identical to a paid one is
                      worse than not showing it, because the test gets run. */}
                  {isUnpaidLab(lab) && (
                    <Chip
                      label={unpaidPolicy.canServeUnpaid
                        ? 'UNPAID · may run'
                        : 'UNPAID · payment required'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: unpaidPolicy.canServeUnpaid
                          ? 'rgba(217,119,6,0.14)'
                          : 'rgba(220,38,38,0.12)',
                        color: unpaidPolicy.canServeUnpaid ? '#A16207' : '#991B1B',
                      }}
                    />
                  )}

                  {/* Results-already-entered chip. The backend keeps the
                      lab_request row around after /saveLabResults so the
                      tech can review or edit; this chip is the visual
                      cue that the test isn't pending anymore. */}
                  {lab.has_results && (
                    <Chip
                      label="Results Entered"
                      size="small"
                      sx={{
                        bgcolor: '#dcfce7',
                        color: '#166534',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                      }}
                    />
                  )}

                  {/* Show the entry window period */}
                  {lab.time_period && (
                    <Chip
                      label={`${lab.time_period} day${lab.time_period > 1 ? 's' : ''} window`}
                      size="small"
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  
                  {/* Show remaining hours for entry with color coding */}
                  {lab.entry_status === 'active' && 
                   lab.hours_since_request !== undefined && 
                   lab.allowed_hours !== undefined && (
                    <Chip 
                      label={`${Math.max(0, Math.round(lab.allowed_hours - lab.hours_since_request))}h left`}
                      size="small"
                      color={
                        (lab.allowed_hours - lab.hours_since_request) < 6 ? 'error' :      // Red if < 6 hours
                        (lab.allowed_hours - lab.hours_since_request) < 12 ? 'warning' :   // Orange if < 12 hours
                        'success'                                                           // Green otherwise
                      }
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Lab #: {lab.lab_no} • Requested: {lab.requested_date}
                    {lab.requested_time && ` at ${lab.requested_time}`} • By: {lab.requ_by}
                  </Typography>
                  {lab.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      Notes: {lab.notes}
                    </Typography>
                  )}
                </Box>
              }
            />
            <Box display="flex" flexDirection="column" gap={2} sx={{ p: 2, ml: 2, alignItems: 'center' }}>
              {/* Reason given BEFORE the click, rather than a 409 after it. */}
              <Tooltip
                title={isBlockedLab(lab)
                  ? 'Payment has not been recorded for this test. Results cannot be entered yet.'
                  : ''}
                disableHoverListener={!isBlockedLab(lab)}
              >
              <span>
              <Button
                variant="contained"
                size="small"
                disabled={isBlockedLab(lab)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEnterResults(lab);
                }}
                sx={{
                  minWidth: 'auto',
                  px: 3,
                  py: 1,
                  // Completed tests get a cooler slate gradient so the row
                  // reads at-a-glance as "done, click to review" rather
                  // than "needs action".
                  background: lab.has_results
                    ? 'linear-gradient(135deg, #475569 0%, #64748b 100%)'
                    : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  '&:hover': {
                    background: lab.has_results
                      ? 'linear-gradient(135deg, #334155 0%, #475569 100%)'
                      : 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                  }
                }}
              >
                {lab.has_results ? 'View / Edit Results' : 'Enter Results'}
              </Button>
              </span>
              </Tooltip>
            </Box>
          </ListItemButton>
          {index < Math.min(rowsPerPage, patientLabs.length - page * rowsPerPage) - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
    
    {patientLabs.length > rowsPerPage && (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, patientLabs.length)} of {patientLabs.length} tests
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              variant="outlined"
              size="small"
            >
              Previous
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                Page {page + 1} of {Math.ceil(patientLabs.length / rowsPerPage)}
              </Typography>
            </Box>
            <Button
              disabled={page >= Math.ceil(patientLabs.length / rowsPerPage) - 1}
              onClick={() => setPage(page + 1)}
              variant="outlined"
              size="small"
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Box>
    )}
  </>
)}
          </CardContent>
        </Card>
      )}


      {/* Results Entry Dialog */}
    <Dialog
      open={resultsDialogOpen}
      onClose={() => setResultsDialogOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '90vh',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            margin: -3,
            marginBottom: 2,
            borderRadius: '12px 12px 0 0'
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Enter Laboratory Results
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {selectedTestForResults?.test_name}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setResultsDialogOpen(false)}
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ bgcolor: '#f1fcf9', p: 3 }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Sections 1 & 2: Parameters List and Entry Form Side by Side */}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* Section 1: Parameters List (Left Side - 6 columns) */}
            <Grid item xs={12} md={6} size={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  border: '1px solid #c8e6c9',
                  background: 'white',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.2rem'
                    }}
                  >
                    1
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1b5e20">
                      Select Parameter
                    </Typography>
                    {testCategory && (
                      <Typography variant="caption" color="text.secondary">
                        Category: {testCategory}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                {loadingParameters ? (
                  <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress sx={{ color: '#11998e' }} />
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      height: 400, 
                      overflow: 'auto', 
                      border: '1px solid #c8e6c9', 
                      borderRadius: 2,
                      bgcolor: '#f1fcf9'
                    }}
                  >
                    <List dense>
                      {testParameters.map((param, index) => (
                        <ListItemButton
                          key={index}
                          selected={selectedParameter?.test === param.test}
                          onClick={() => handleParameterSelect(param)}
                          disabled={editingIndex !== null}
                          sx={{
                            borderBottom: '1px solid #e8f5e9',
                            '&:last-child': { borderBottom: 0 },
                            '&.Mui-selected': {
                              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                              color: 'white',
                              '& .MuiListItemText-secondary': {
                                color: 'rgba(255, 255, 255, 0.8)'
                              },
                              '&:hover': { 
                                background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)'
                              }
                            },
                            '&.Mui-disabled': {
                              opacity: 0.5
                            },
                            '&:hover': {
                              bgcolor: '#f1fcf9'
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography fontWeight={600}>
                                {param.test}
                              </Typography>
                            }
                            secondary={`Category: ${param.cat}`}
                          />
                          {loadingParameterDetails && selectedParameter?.test === param.test && (
                            <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />
                          )}
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Section 2: Parameter Entry Form (Right Side - 6 columns) */}
            <Grid item xs={12} md={6} size={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  border: '1px solid #c8e6c9',
                  background: 'white',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.2rem'
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="#1b5e20">
                    Enter Result
                  </Typography>
                </Box>
                
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Selected Parameter"
                    value={selectedParameter?.test || ''}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#f1fcf9'
                      }
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Unit"
                    value={selectedParameter?.unit || ''}
                    onChange={(e) => {
                      if (selectedParameter) {
                        setSelectedParameter({
                          ...selectedParameter,
                          unit: e.target.value
                        });
                      }
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#11998e',
                            borderWidth: 2
                          }
                        }
                      }
                    }}
                    placeholder="Enter unit"
                  />
                  
                  <TextField
                    fullWidth
                    label="Reference Range"
                    value={selectedParameter ? getParameterRange(selectedParameter) : ''}
                    onChange={(e) => {
                      if (selectedParameter) {
                        // Update the appropriate range field based on patient gender
                        const patientGender = selectedPatient?.gender?.toUpperCase();
                        const updatedParameter = { ...selectedParameter };
                        
                        if (patientGender === 'MALE') {
                          updatedParameter.male = e.target.value;
                        } else if (patientGender === 'FEMALE') {
                          updatedParameter.female = e.target.value;
                        } else {
                          updatedParameter.children = e.target.value;
                        }
                        
                        setSelectedParameter(updatedParameter);
                      }
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#11998e',
                            borderWidth: 2
                          }
                        }
                      }
                    }}
                    placeholder="Enter reference range"
                  />
                  
                  <FormControl fullWidth size="small">
                    <InputLabel>Flag</InputLabel>
                    <Select
                      value={parameterFlag}
                      label="Flag"
                      onChange={(e) => handleFlagChange(e.target.value)}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c8e6c9'
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#11998e',
                            borderWidth: 2
                          }
                        }
                      }}
                    >
                      {availableFlags.map((flag) => (
                        <MenuItem key={flag} value={flag}>
                          {flag === 'Select/Enter' ? flag : flag.charAt(0).toUpperCase() + flag.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Result Value"
                    value={parameterResult}
                    onChange={(e) => handleResultChange(e.target.value)}
                    size="small"
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#11998e',
                            borderWidth: 2
                          }
                        }
                      }
                    }}
                  />
                  
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleAddResult}
                      disabled={!selectedParameter || !parameterResult.trim()}
                      fullWidth
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                          boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
                        },
                        '&:disabled': {
                          background: '#e0e0e0',
                          color: '#9e9e9e'
                        }
                      }}
                    >
                      {editingIndex !== null ? '✓ Update Result' : '+ Add Result'}
                    </Button>
                    {editingIndex !== null ? (
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        fullWidth
                        sx={{
                          py: 1,
                          borderRadius: 2,
                          borderColor: '#11998e',
                          color: '#11998e',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#0e8679',
                            bgcolor: 'rgba(17, 153, 142, 0.04)'
                          }
                        }}
                      >
                        Cancel Edit
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={handleClearResult}
                        fullWidth
                        sx={{
                          py: 1,
                          borderRadius: 2,
                          borderColor: '#c8e6c9',
                          color: '#666',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#11998e',
                            bgcolor: 'rgba(17, 153, 142, 0.04)'
                          }
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 3: Results Table */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid #c8e6c9',
              background: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.2rem'
                }}
              >
                3
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1b5e20">
                  Added Results
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {enteredResults.length} result(s) added
                </Typography>
              </Box>
            </Box>
            
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{ 
                maxHeight: 300,
                border: '1px solid #c8e6c9',
                borderRadius: 2
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', bgcolor: '#f1fcf9' }}>
                      Parameter
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', bgcolor: '#f1fcf9' }}>
                      Result
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', bgcolor: '#f1fcf9' }}>
                      Unit
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', bgcolor: '#f1fcf9' }}>
                      Range
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', bgcolor: '#f1fcf9' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1b5e20', bgcolor: '#f1fcf9' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enteredResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No results added yet. Add your first result above.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    enteredResults.map((result, index) => (
                      <TableRow 
                        key={index}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: '#f1fcf9'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          {result.parameterName}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            fontWeight={700}
                            sx={{
                              color: result.flag === 'normal' 
                                ? '#2e7d32' 
                                : result.flag === 'high'
                                ? '#d32f2f'
                                : result.flag === 'low'
                                ? '#ed6c02'
                                : 'text.primary'
                            }}
                          >
                            {result.result}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {result.unit}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {result.range}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={result.flag}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: result.flag === 'normal'
                                ? 'rgba(46, 125, 50, 0.1)'
                                : result.flag === 'high'
                                ? 'rgba(211, 47, 47, 0.1)'
                                : result.flag === 'low'
                                ? 'rgba(237, 108, 2, 0.1)'
                                : 'rgba(0, 0, 0, 0.08)',
                              color: result.flag === 'normal'
                                ? '#2e7d32'
                                : result.flag === 'high'
                                ? '#d32f2f'
                                : result.flag === 'low'
                                ? '#ed6c02'
                                : 'text.primary',
                              border: 'none'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditResult(index)}
                              disabled={editingIndex !== null}
                              sx={{
                                color: '#11998e',
                                '&:hover': {
                                  bgcolor: 'rgba(17, 153, 142, 0.1)'
                                },
                                '&:disabled': {
                                  color: '#e0e0e0'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveResult(result)}
                              disabled={editingIndex !== null}
                              sx={{
                                color: '#d32f2f',
                                '&:hover': {
                                  bgcolor: 'rgba(211, 47, 47, 0.1)'
                                },
                                '&:disabled': {
                                  color: '#e0e0e0'
                                }
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Section 4: General Comments */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid #c8e6c9',
              background: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.2rem'
                }}
              >
                4
              </Box>
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                General Comments
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Notes or Comments"
              value={generalComment}
              onChange={(e) => setGeneralComment(e.target.value)}
              placeholder="Enter any general comments, observations, or notes for this lab test..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#11998e',
                      borderWidth: 2
                    }
                  }
                }
              }}
            />
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, bgcolor: '#f1fcf9', gap: 2 }}>
        <Button 
          onClick={() => setResultsDialogOpen(false)}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            color: '#666',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveResults}
          disabled={enteredResults.length === 0}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
              boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
            },
            '&:disabled': {
              background: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          💾 Save All Results ({enteredResults.length})
        </Button>
      </DialogActions>
    </Dialog>

    
    
      {/* Documentation Dialog */}
      <Dialog
        open={documentationDialogOpen}
        onClose={handleCloseDocumentation}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 3, py: 2.25,
              color: 'white',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
              fontWeight: 800, fontSize: '1.1rem',
            }}>
              {(selectedPatient?.fullName || '?')
                .split(/\s+/).filter(Boolean).slice(0, 2)
                .map(n => n[0]).join('').toUpperCase()}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', opacity: 0.85, lineHeight: 1,
              }}>
                Clinical Notes
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, mt: 0.4, lineHeight: 1.2 }}>
                Patient Documentation
              </Typography>
              {selectedPatient && (
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.9, mt: 0.25 }}>
                  {selectedPatient.fullName} · ID {selectedPatient.id}
                </Typography>
              )}
            </Box>
            {documentationLoading && <CircularProgress size={24} sx={{ color: 'white' }} />}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f8fafc', p: { xs: 2, md: 3 } }}>
          {documentationData ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {[
                { key: 'complaint', label: 'Chief Complaint',       text: documentationData.complaint, color: '#dc2626', tint: '#fef2f2', badge: '1' },
                { key: 'history',   label: 'History of Complaint',  text: documentationData.history,   color: '#2563eb', tint: '#eff6ff', badge: '2' },
                { key: 'physical',  label: 'Physical Examination',  text: documentationData.physical,  color: '#15803d', tint: '#f0fdf4', badge: '3' },
              ].map((s) => (
                <Paper
                  key={s.key}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e5e9ef',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.06)' },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.25,
                    borderLeft: '4px solid', borderLeftColor: s.color,
                    bgcolor: s.tint,
                  }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: s.color, color: 'white',
                      fontWeight: 800, fontSize: '0.85rem',
                      boxShadow: `0 2px 6px ${s.color}40`,
                    }}>
                      {s.badge}
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: s.color,
                      fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px',
                    }}>
                      {s.label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 3, py: 2.25 }}>
                    {s.text && String(s.text).trim() ? (
                      <Typography sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.75,
                        color: '#334155',
                        fontSize: '0.93rem',
                      }}>
                        {s.text}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.88rem' }}>
                        No {s.label.toLowerCase()} recorded for this patient.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%',
                bgcolor: '#e2e8f0', mx: 'auto', mb: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#64748b', fontWeight: 700, fontSize: '1.5rem',
              }}>
                ?
              </Box>
              <Typography sx={{ color: '#334155', fontWeight: 600 }}>
                No documentation available
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>
                Nothing has been recorded for this patient yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: '#f8fafc', px: 3, py: 2, borderTop: '1px solid #e5e9ef' }}>
          <Button
            onClick={handleCloseDocumentation}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.75,
              px: 3, boxShadow: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { boxShadow: '0 4px 12px rgba(102,126,234,0.4)' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>



      <LabReportDialog
      open={reportDialogOpen}
      onClose={handleCloseReport}
      reportData={reportData}
      reportLoading={reportLoading}
      selectedPatient={selectedPatient}
    />

      <MedicalHistoryDialog
        open={medicalHistoryOpen}
        onClose={() => setMedicalHistoryOpen(false)}
        patientData={selectedPatient}
      />
    </Box>
  );
};

export default MyPatients;